import React, { useState, useEffect, useMemo } from "react";
import {
    Tooltip,
    IconButton,
    MenuItem,
    TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Add from "@mui/icons-material/Add";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import {
    CrmPage,
    CrmPageHeader,
    CrmActionButton,
    CrmTableCard,
    CrmNotice,
    CrmFilterBar,
    CrmRowActions,
} from "../shared/CrmUi";

import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PersonIcon from "@mui/icons-material/Person";
import FlagIcon from "@mui/icons-material/Flag";
import AssignmentIcon from "@mui/icons-material/Assignment";

// Durum etiketi: statusMap rengini koruyarak yumuşak tonlu, noktalı hap
const StatusDot = ({ label, color }) => (
    <MDBox
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 1,
            py: 0.25,
            mr: 0.5,
            mb: 0.25,
            borderRadius: "6px",
            fontSize: "11.5px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            color,
            background: `${color}1A`,
        }}
    >
        <MDBox component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
        {label}
    </MDBox>
);

function SalesPipeline() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [salesRepsList, setSalesRepsList] = useState([]);
    const [selectedSalesRep, setSelectedSalesRep] = useState([]);
    const [expanded, setExpanded] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const navigate = useNavigate();
    const [selectedItem, setSelectedItem] = useState(null);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

    const statusMap = {
        Prospect:           { barValue: 20,  labelValue: 20,  color: "#40E0D0" },
        "Needs Assessment": { barValue: 50,  labelValue: 50,  color: "#FF9800" },
        Target:             { barValue: 70,  labelValue: 70,  color: "#053b97" },
        Secured:            { barValue: 100, labelValue: 100, color: "#4CAF50" },
        Lost:               { barValue: 5,   labelValue: 0,   color: "#F44336" },
    };


    // Veri çekme
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await CrudService.getOpportunities();
                const normalizedData = response.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));

                setData(normalizedData);
                setFilteredData(normalizedData);

                const reps = new Set();

                normalizedData.forEach((item) => {
                    const rep = item.salesRep;
                    if (Array.isArray(rep)) {
                        rep.forEach((r) => r?.name && reps.add(r.name));
                    } else if (typeof rep === "object") {
                        rep?.name && reps.add(rep.name);
                    } else if (typeof rep === "string") {
                        reps.add(rep);
                    }
                });
                setSalesRepsList(["All", ...Array.from(reps)]);

            } catch (error) {
                console.error("Error fetching opportunities:", error);
                setNotification({
                    value: true,
                    text: "Failed to load opportunities. Please try again later.",
                });
            }
        };

        fetchData();
    }, []);

    // Filtreleme değiştiğinde çalışır
    useEffect(() => {
        if (selectedSalesRep.length === 0 || selectedSalesRep.includes("All")) {
            setFilteredData(data);
        } else {
            setFilteredData(
                data.filter((item) => {
                    const rep = item.salesRep;
                    if (Array.isArray(rep)) {
                        return rep.some((r) => selectedSalesRep.includes(r.name));
                    }
                    if (typeof rep === "object") {
                        return selectedSalesRep.includes(rep.name);
                    }
                    return selectedSalesRep.includes(rep);
                })
            );
        }
    }, [selectedSalesRep, data]);


    const toggleExpand = (id) => {
        setExpanded((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const tableData = useMemo(() => {
        const summary = {};
        const details = {};

        filteredData.forEach((item) => {
            const customer = item.customer || "Unknown Customer";
            const revenue = parseFloat(item.totalRevenue) || 0;
            const currency = item.freightRateCurrency || "N/A";

            if (!summary[customer]) {
                summary[customer] = {
                    customer,
                    totalOpportunities: 0,
                    transportTypes: new Set(),
                    statuses: new Set(),
                    salesReps: new Set(),
                    revenueByCurrency: {},
                    opportunityNames: new Set(),
                };
                details[customer] = [];
            }

            summary[customer].totalOpportunities += 1;
            summary[customer].transportTypes.add(item.selectedTransportType || "N/A");
            const reps = Array.isArray(item.salesRep)
                ? item.salesRep.map((r) => r?.name)
                : typeof item.salesRep === "object"
                    ? [item.salesRep?.name]
                    : [item.salesRep];

            reps.forEach((r) => {
                if (r) summary[customer].salesReps.add(r);
            });
            summary[customer].salesReps.add(item.salesRep || "N/A");
            summary[customer].opportunityNames.add(item.opportunityName || "Unnamed");

            if (!summary[customer].revenueByCurrency[currency]) {
                summary[customer].revenueByCurrency[currency] = 0;
            }
            summary[customer].revenueByCurrency[currency] += revenue;

            details[customer].push(item);
        });

        const mainRows = Object.values(summary).map((item) => {
            const customer = item.customer;
            const revenueList = Object.entries(item.revenueByCurrency).map(
                ([currency, amount]) => `${amount.toFixed(2)} ${currency}`
            );

            return {
                id: customer,
                expand: (
                    <IconButton size="small" onClick={() => toggleExpand(customer)}>
                        {expanded.includes(customer) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                ),
                customer: (
                    <Tooltip title={customer}>
                        <span>{customer.length > 25 ? customer.slice(0, 25) + "..." : customer}</span>
                    </Tooltip>
                ),
                opportunityName: Array.from(item.opportunityNames).join(", "),
                salesReps: Array.from(item.salesReps)
                    .map(rep => (typeof rep === "object" ? rep?.name : rep))
                    .filter(Boolean)
                    .join(", "),
                statuses: (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {Array.from(item.statuses).map((status, idx) => {
                            const color = statusMap[status]?.color || "#98A2B3";
                            return <StatusDot key={idx} label={status} color={color} />;
                        })}
                    </Stack>
                ),
                totalOpportunities: item.totalOpportunities,
                transportTypes: Array.from(item.transportTypes).join(", "),
                totalRevenue: revenueList.join(", "),
                _details: details[customer],
            };
        });

        const finalRows = [];
        mainRows.forEach((row) => {
            finalRows.push(row);
            if (expanded.includes(row.id)) {
                row._details.forEach((item, index) => {
                    finalRows.push({
                        id: `${row.id}-detail-${index}`,
                        expand: "",
                        customer: "",
                        opportunityName: <strong>{item.opportunityName}</strong>,
                        salesReps: Array.isArray(item.salesRep)
                            ? item.salesRep.map((r) => r.name).join(", ")
                            : (item.salesRep?.name || item.salesRep || "-"),
                        statuses: <StatusDot label={item.status} color={statusMap[item.status]?.color || "#98A2B3"} />,
                        totalOpportunities: "",
                        transportTypes: item.selectedTransportType,
                        totalRevenue: (
                            <CrmRowActions
                                editTitle="Edit opportunity"
                                onEdit={() => navigate(`/opportunity/create-opportunity/details?id=${item.id}`)}
                            >
                                <CrmActionButton
                                    variant="secondary"
                                    icon={<VisibilityOutlined />}
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setReviewDialogOpen(true);
                                    }}
                                >
                                    Review
                                </CrmActionButton>
                            </CrmRowActions>
                        ),

                    });
                });
            }
        });

        return finalRows;
    }, [filteredData, expanded, navigate]);

    const columns = [
        { Header: "", accessor: "expand", width: "5%" },
        {
            Header: "Customer",
            accessor: "customer",
        },
        {
            Header: "Opportunity",
            accessor: "opportunityName",
        },
        {
            Header: "Sales Rep",
            accessor: "salesReps",
        },
        {
            Header: "Status",
            accessor: "statuses",
        },
        {
            Header: "Total Opp",
            accessor: "totalOpportunities",
        },
        {
            Header: "Transport Types",
            accessor: "transportTypes",
        },
        {
            Header: "Total Revenue",
            accessor: "totalRevenue",
        },
    ];


    const clickAddHandler = () => {
        navigate("/create-opportunity");
    };

    useEffect(() => {
        if (selectedSalesRep.length === 0 || selectedSalesRep.includes("All")) {
            setFilteredData(data);
        } else {
            setFilteredData(
                data.filter((item) => {
                    const rep = item.salesRep;
                    if (Array.isArray(rep)) {
                        return rep.some((r) => selectedSalesRep.includes(r.name));
                    }
                    if (typeof rep === "object") {
                        return selectedSalesRep.includes(rep.name);
                    }
                    return selectedSalesRep.includes(rep);
                })
            );
        }
    }, [selectedSalesRep, data]);


    const handleChange = (event) => {
        setSelectedSalesRep(event.target.value);
    };

    const handleDelete = (repToDelete) => {
        setSelectedSalesRep((prev) =>
            prev.filter((rep) => rep !== repToDelete)
        );
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Sales Pipeline"
                    subtitle="Opportunities grouped by customer, with status, transport types and revenue by currency."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                            Create Opportunity
                        </CrmActionButton>
                    }
                />
                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}

                    <CrmFilterBar>
                        {/* Dropdown */}
                        <TextField
                            select
                            size="small"
                            label="Select Sales Reps"
                            value={selectedSalesRep}
                            onChange={handleChange}
                            SelectProps={{
                                multiple: true,
                            }}
                            sx={{ width: 320 }}
                        >
                            {salesRepsList.map((rep) => (
                                <MenuItem key={rep} value={rep}>
                                    {rep}
                                </MenuItem>
                            ))}
                        </TextField>

                        {/* Chip List */}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {selectedSalesRep.map((rep) => (
                                <Chip
                                    key={rep}
                                    label={rep}
                                    size="small"
                                    onDelete={() => handleDelete(rep)}
                                    sx={{ fontWeight: 600, borderRadius: "6px", background: "#EEF2F6" }}
                                />
                            ))}
                        </Stack>
                    </CrmFilterBar>

                    {/* Tablo */}
                    <CrmTableCard title="Pipeline by Customer" summary="Expand a customer to see its opportunities">
                        <DataTable table={{ columns, rows: tableData }} canSearch />
                    </CrmTableCard>
            </CrmPage>
            <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ backgroundColor: "#0B1F3A", color: "white", py: 2 }}>
                    <Box mt={2} mb={1}>
                        <MDTypography
                            variant="subtitle2"
                            color="white"
                            fontWeight="bold"
                            sx={{ textAlign: 'center' }}
                        >
                            GENERAL INFORMATION
                        </MDTypography>

                        <Divider sx={{ borderColor: "#a1d1da", my: 1 }} />
                    </Box>
                </DialogTitle>


                <DialogContent dividers sx={{ backgroundColor: "#f5f8fa" }}>
                    {selectedItem ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AssignmentIcon color="primary" />
                                <MDTypography variant="body1">
                                    <strong>Customer:</strong> {selectedItem.customer}
                                </MDTypography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <FlagIcon color="secondary" />
                                <MDTypography variant="body1">
                                    <strong>Opportunity:</strong> {selectedItem.opportunityName}
                                </MDTypography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon color="success" />
                                <MDTypography variant="body1">
                                    <strong>Pickup Country:</strong> {selectedItem.pickupLocation.selectedPickupCountry}
                                </MDTypography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon color="error" />
                                <MDTypography variant="body1">
                                    <strong>Delivery Country:</strong> {selectedItem.deliveryLocation.selectedDeliveryCountry}
                                </MDTypography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <PersonIcon color="warning" />
                                <MDTypography variant="body1">
                                    <strong>Sales Rep:</strong>{" "}
                                    {Array.isArray(selectedItem.salesRep)
                                        ? selectedItem.salesRep.map((r) => r?.name).join(", ")
                                        : selectedItem.salesRep?.name || selectedItem.salesRep || "-"}
                                </MDTypography>
                            </Box>


                            <Box display="flex" alignItems="center" gap={1}>
                                <FlagIcon color="info" />
                                <MDTypography variant="body1">
                                    <strong>Status:</strong> {selectedItem.status}
                                </MDTypography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <LocalShippingIcon color="secondary" />
                                <MDTypography variant="body1">
                                    <strong>Transport Type:</strong> {selectedItem.selectedTransportType}
                                </MDTypography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <MonetizationOnIcon sx={{ color: "#061a9e" }} />
                                <MDTypography variant="body1">
                                    <strong>Revenue:</strong> {selectedItem.totalRevenue} {selectedItem.freightRateCurrency}
                                </MDTypography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <MDTypography variant="body2" color="text" fontStyle="italic">
                                For all other information, please use the <strong>Edit</strong> button.
                            </MDTypography>
                        </Box>
                    ) : (
                        <MDTypography>Loading...</MDTypography>
                    )}
                </DialogContent>

                <DialogActions sx={{ backgroundColor: "#f0f0f0" }}>
                    <MDButton onClick={() => setReviewDialogOpen(false)} color="error" variant="outlined">
                        Close
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default SalesPipeline;
